import type { MediaGrant } from './api.ts';

/**
 * Il media lato volontario.
 *
 * Il client si carica solo quando una chiamata parte davvero: un volontario che
 * apre la console per prenotare un turno non ha motivo di scaricare mezzo
 * megabyte, e la console si apre spesso dal telefono.
 *
 * Il flusso passa **sempre** da un SFU. Non esiste qui, e non deve esistere
 * altrove, un percorso che colleghi direttamente i due browser: esporrebbe
 * l'indirizzo IP del volontario a chi chiama, che è la persona che un giorno
 * potrebbe essere bloccata per molestie (sezione 7).
 *
 * Dalla fase 5 regge anche il **video**, e non per riuso: è lo stesso mestiere
 * con una traccia in più. Due client media vorrebbero dire due modi di gestire
 * una riconnessione e due modi di sbagliare la chiusura di una room.
 */

export type CallEvent =
  | { type: 'connected' }
  | { type: 'reconnecting' }
  | { type: 'disconnected' }
  | { type: 'microphone_blocked' }
  | { type: 'camera_blocked' }
  /** Chi ha chiamato ha acceso o spento la propria camera. */
  | { type: 'remote_video'; present: boolean }
  | { type: 'failed' };

export type CallSession = {
  disconnect: () => Promise<void>;
  setMuted: (muted: boolean) => Promise<void>;
  /** Spegne la camera senza toccare l'audio né chiudere la conversazione. */
  setCameraEnabled: (enabled: boolean) => Promise<boolean>;
};

export type CallSurfaces = {
  /** Dove finiscono gli elementi audio, che restano invisibili. */
  sink: HTMLElement;
  /** Il video di chi ha chiamato. Solo sul canale video. */
  remoteVideo?: HTMLVideoElement | null;
  /** La propria immagine, in un angolo. */
  localVideo?: HTMLVideoElement | null;
  withVideo?: boolean;
  /**
   * Il microfono va pubblicato **spento**?
   *
   * Una funzione e non un valore, perché la risposta cambia mentre questo
   * codice gira: il pulsante esiste da prima del collegamento, e chi lo preme
   * lo preme proprio adesso. Con un valore letto all'inizio, il microfono
   * verrebbe acceso lo stesso e resterebbe acceso fino alla fine del
   * collegamento — con l'etichetta che dice già il contrario.
   */
  muted?: () => boolean;
};

export async function connectToCall(
  grant: MediaGrant,
  surfaces: CallSurfaces,
  onEvent: (event: CallEvent) => void,
): Promise<CallSession> {
  const sink = surfaces.sink;
  const { Room, RoomEvent, Track } = await import('livekit-client');

  /**
   * `adaptiveStream` solo sul video: riduce la qualità ricevuta quando
   * l'elemento è piccolo o nascosto, ed è ciò che rende sostenibile una
   * videochiamata dal telefono di un volontario. Sull'audio non ha niente da
   * adattare.
   */
  const room = new Room({ adaptiveStream: Boolean(surfaces.withVideo), dynacast: false });

  room.on(RoomEvent.Connected, () => onEvent({ type: 'connected' }));
  room.on(RoomEvent.Reconnecting, () => onEvent({ type: 'reconnecting' }));
  room.on(RoomEvent.Reconnected, () => onEvent({ type: 'connected' }));
  room.on(RoomEvent.Disconnected, () => onEvent({ type: 'disconnected' }));

  room.on(RoomEvent.TrackSubscribed, (track) => {
    if (track.kind === Track.Kind.Audio) {
      const element = track.attach();
      element.setAttribute('autoplay', '');
      element.style.display = 'none';
      sink.appendChild(element);
      return;
    }
    if (track.kind === Track.Kind.Video && surfaces.remoteVideo) {
      track.attach(surfaces.remoteVideo);
      onEvent({ type: 'remote_video', present: true });
    }
  });

  room.on(RoomEvent.TrackUnsubscribed, (track) => {
    track.detach().forEach((element) => {
      if (element !== surfaces.remoteVideo) element.remove();
    });
    if (track.kind === Track.Kind.Video) onEvent({ type: 'remote_video', present: false });
  });

  try {
    await room.connect(grant.url, grant.token);
  } catch {
    onEvent({ type: 'failed' });
    throw new Error('media_connect_failed');
  }

  /** Cosa si è chiesto **adesso**, non a inizio collegamento. */
  const wantsMuted = (): boolean => surfaces.muted?.() ?? false;

  try {
    await room.localParticipant.setMicrophoneEnabled(!wantsMuted());
  } catch {
    // Il permesso del microfono è il blocco più comune al primo turno. Va detto
    // e risolto, non trattato come un errore fatale della chiamata.
    onEvent({ type: 'microphone_blocked' });
  }

  const attachLocal = (): void => {
    const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
    if (publication?.track && surfaces.localVideo) publication.track.attach(surfaces.localVideo);
  };

  if (surfaces.withVideo) {
    try {
      await room.localParticipant.setCameraEnabled(true);
      attachLocal();
    } catch {
      /**
       * La camera negata non chiude la videochiamata: si resta in
       * conversazione con la sola voce, e la schermata lo dice. È lo stesso
       * criterio del microfono, e la persona dall'altra parte ha già aspettato
       * abbastanza per parlare con qualcuno.
       */
      onEvent({ type: 'camera_blocked' });
    }
  }

  /**
   * E si ricontrolla **prima di restituire la sessione**.
   *
   * Fra la pubblicazione del microfono e questa riga può passare parecchio: su
   * una videochiamata in mezzo c'è il permesso della camera, che è una
   * finestra del browser e dura quanto ci mette una persona a rispondere. Chi
   * preme "disattiva il microfono" in quei secondi, senza questo, parla in un
   * microfono acceso con scritto che è spento.
   *
   * L'errore non chiude la conversazione — una sessione attiva non si
   * interrompe per nessun motivo (vincolo 5) — e non resta muto: chi ha
   * chiamato `connectToCall` riapplica lo stato appena riceve la sessione, e
   * quella è la strada che lo dice a chi risponde (`ActiveCall`).
   */
  if (wantsMuted()) {
    try {
      await room.localParticipant.setMicrophoneEnabled(false);
    } catch {
      // Lo riprova, e lo dice, chi ha chiesto il collegamento.
    }
  }

  return {
    disconnect: async () => {
      await room.disconnect();
      sink.replaceChildren();
      if (surfaces.remoteVideo) surfaces.remoteVideo.srcObject = null;
      if (surfaces.localVideo) surfaces.localVideo.srcObject = null;
    },
    setMuted: async (muted: boolean) => {
      await room.localParticipant.setMicrophoneEnabled(!muted);
    },
    setCameraEnabled: async (enabled: boolean) => {
      try {
        await room.localParticipant.setCameraEnabled(enabled);
        if (enabled) attachLocal();
        else if (surfaces.localVideo) surfaces.localVideo.srcObject = null;
        return enabled;
      } catch {
        onEvent({ type: 'camera_blocked' });
        return false;
      }
    },
  };
}
